import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import axiosInstance from '../api/axios';
import { FileText, Plus, X, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

const Invoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { register, handleSubmit, reset, formState: { errors } } = useForm();

    const fetchInvoices = async (page = 1) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/invoices?page=${page}`);
            setInvoices(response.data.data);
            setPagination(response.data);
        } catch (error) { toast.error('Error fetching invoices'); }
        finally { setLoading(false); }
    };

    const fetchClientsForDropdown = async () => {
        try {
            const response = await axiosInstance.get('/clients');
            setClients(response.data.data);
        } catch (error) { }
    };

    useEffect(() => {
        fetchInvoices();
        fetchClientsForDropdown();
    }, []);

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            await axiosInstance.post('/invoices', data);
            toast.success('Invoice created successfully! 💸');
            reset(); setIsModalOpen(false); fetchInvoices(1);
        } catch (error) { toast.error('Error saving invoice'); }
        finally { setIsSubmitting(false); }
    };

    const downloadPdf = async (id) => {
        const loadingToast = toast.loading('Preparing PDF...');
        try {
            const response = await axiosInstance.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `INV-${id}.pdf`);
            document.body.appendChild(link); link.click(); link.remove();
            toast.success('Downloaded successfully!', { id: loadingToast });
        } catch (error) { toast.error('Error downloading PDF', { id: loadingToast }); }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'paid': return <span style={{ backgroundColor: '#d1fae5', color: '#059669', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>PAID</span>;
            case 'pending': return <span style={{ backgroundColor: '#fef3c7', color: '#d97706', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>PENDING</span>;
            case 'cancelled': return <span style={{ backgroundColor: '#fee2e2', color: '#ef4444', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>CANCELLED</span>;
            default: return status;
        }
    };

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FileText size={28} color="#10b981" /> Billing & Invoices
                    </h2>
                    <p style={{ margin: '5px 0 0 0', color: '#64748b', fontSize: '14px' }}>Create, manage, and download PDF invoices.</p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)} className="action-btn"
                    style={{ backgroundColor: '#10b981', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)' }}
                >
                    <Plus size={20} /> Create Invoice
                </button>
            </div>

            <div className="glass-card" style={{ padding: '30px' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading data...</div>
                ) : (
                    <>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                                    <th style={{ padding: '15px 0', color: '#64748b', fontWeight: '600' }}>Invoice ID</th>
                                    <th style={{ padding: '15px 0', color: '#64748b', fontWeight: '600' }}>Client</th>
                                    <th style={{ padding: '15px 0', color: '#64748b', fontWeight: '600' }}>Amount</th>
                                    <th style={{ padding: '15px 0', color: '#64748b', fontWeight: '600' }}>Status</th>
                                    <th style={{ padding: '15px 0', color: '#64748b', fontWeight: '600' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((invoice, index) => (
                                    <motion.tr
                                        initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }}
                                        key={invoice.id} style={{ borderBottom: '1px solid #f8fafc' }}
                                    >
                                        <td style={{ padding: '20px 0', fontWeight: '700', color: '#0f172a' }}>INV-{String(invoice.id).padStart(4, '0')}</td>
                                        <td style={{ padding: '20px 0', fontWeight: '500', color: '#475569' }}>{invoice.client?.name || 'Deleted Client'}</td>
                                        <td style={{ padding: '20px 0', color: '#334155', fontWeight: '700' }}>${invoice.amount.toLocaleString()}</td>
                                        <td style={{ padding: '20px 0' }}>{getStatusBadge(invoice.status)}</td>
                                        <td style={{ padding: '20px 0' }}>
                                            <button onClick={() => downloadPdf(invoice.id)} className="action-btn" style={{ padding: '8px 12px', backgroundColor: '#f1f5f9', color: '#3b82f6', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: '600', fontSize: '13px' }}>
                                                <Download size={16} /> PDF
                                            </button>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            <AnimatePresence>
                {isModalOpen && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 50 }}>
                        <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="glass-card" style={{ padding: '30px', width: '400px', maxWidth: '90%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Create Invoice</h3>
                                <button onClick={() => setIsModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={24} /></button>
                            </div>
                            <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#475569' }}>Select Client</label>
                                    <select {...register('client_id', { required: 'Client is required' })} style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none', backgroundColor: 'white' }}>
                                        <option value="">-- Choose --</option>
                                        {clients.map(client => <option key={client.id} value={client.id}>{client.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#475569' }}>Amount ($)</label>
                                    <input type="number" step="0.01" {...register('amount', { required: 'Amount required', min: 1 })} style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#475569' }}>Status</label>
                                    <select {...register('status')} style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none', backgroundColor: 'white' }}>
                                        <option value="pending">Pending</option>
                                        <option value="paid">Paid</option>
                                    </select>
                                </div>
                                <div style={{ marginTop: '20px' }}>
                                    <button type="submit" disabled={isSubmitting} className="action-btn" style={{ width: '100%', padding: '12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                                        {isSubmitting ? 'Processing...' : 'Generate Invoice'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default Invoices;